import React, { useRef, useState, useEffect } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

interface HouseholdBudgetViewProps {
  className?: string;
  leftImageUrl?: string;
  isEditMode?: boolean;
  onLeftImageChange?: (url: string | null) => void;
  formValues?: Record<string, any>;
  onFieldChange?: (fieldId: string, value: any) => void;
}


export function HouseholdBudgetView({
  className,
  leftImageUrl,
  isEditMode = false,
  onLeftImageChange,
  formValues = {},
  onFieldChange,
}: HouseholdBudgetViewProps) {
  const leftFileInputRef = useRef<HTMLInputElement>(null);
  const [localLeftImage, setLocalLeftImage] = useState<string | null>(leftImageUrl || null);
  const [isUploadingLeft, setIsUploadingLeft] = useState(false);
  const [imageScale, setImageScale] = useState(100);

  const freiesKapital = formValues.budget_freies_kapital || "";
  const monatlicheZiel = formValues.budget_monatliche_ziel || "";

  useEffect(() => {
    setLocalLeftImage(leftImageUrl || null);
  }, [leftImageUrl]);

  const handleFileUpload = async (file: File) => {
    setIsUploadingLeft(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `household-illustrations/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalLeftImage(publicUrl);
      onLeftImageChange?.(publicUrl);
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setIsUploadingLeft(false);
    }
  };

  const handleRemoveImage = () => {
    setLocalLeftImage(null);
    onLeftImageChange?.(null);
    setImageScale(100);
  };

  return (
    <div className={cn("p-6 space-y-6", className)}>
      {/* Title section */}
      <div className="space-y-1">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Finanzielle Ziele </span>
          <span className="text-primary">erreichen</span>
        </h2>
        <p className="text-lg italic text-muted-foreground">
          Smarte Lebenszyklusplanung.
        </p>
      </div>

      {/* Image Upload Area */}
      <div className="border-2 border-primary rounded-lg overflow-hidden min-h-[300px] flex items-center justify-center bg-muted/20">
        <input
          ref={leftFileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
          }}
        />
        
        {localLeftImage ? (
          <div className="relative w-full h-full flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
              <img
                src={localLeftImage}
                alt="Haushaltsplanung Grafik"
                className="max-w-full max-h-full object-contain transition-transform"
                style={{ transform: `scale(${imageScale / 100})` }}
              />
            </div>
            {isEditMode && (
              <div className="p-4 border-t border-border space-y-3">
                <div className="flex items-center gap-4">
                  <Label className="text-sm whitespace-nowrap">Skalierung:</Label>
                  <Slider
                    value={[imageScale]}
                    onValueChange={([val]) => setImageScale(val)}
                    min={50}
                    max={200}
                    step={5}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground w-12">{imageScale}%</span>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveImage}
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  Bild entfernen
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div
            className={cn(
              "flex flex-col items-center justify-center gap-4 p-8 text-muted-foreground",
              isEditMode && "cursor-pointer hover:text-foreground transition-colors"
            )}
            onClick={() => isEditMode && leftFileInputRef.current?.click()}
          >
            <Upload className="h-12 w-12" />
            <span className="text-center">
              {isEditMode ? "Klicken zum Hochladen" : "Keine Grafik verfügbar"}
            </span>
            {isUploadingLeft && <span className="text-sm">Wird hochgeladen...</span>}
          </div>
        )}
      </div>

      {/* Input fields */}
      <div className="space-y-4 pt-4">
        <div className="flex items-center justify-between gap-4">
          <Label className="text-sm font-medium">
            Freies Kapital (nach Haushaltskostenrechnung)
          </Label>
          <div className="flex items-center gap-2">
            <span>€</span>
            <Input
              value={freiesKapital}
              onChange={(e) => onFieldChange?.("budget_freies_kapital", e.target.value)}
              className="w-32 h-8 text-right"
              placeholder=""
            />
            <span>,-</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between gap-4">
          <Label className="text-sm font-medium">
            Wie viel möchten Sie monatlich für die Erreichung Ihrer Ziele sparen?
          </Label>
          <div className="flex items-center gap-2">
            <span>€</span>
            <Input
              value={monatlicheZiel}
              onChange={(e) => onFieldChange?.("budget_monatliche_ziel", e.target.value)}
              className="w-32 h-8 text-right"
              placeholder=""
            />
            <span>,-</span>
          </div>
        </div>
      </div>
    </div>
  );
}
